<script lang="ts" setup>
import { computed, ref } from "vue";
import type { PartnerIssue } from "@/types/project-types";
import OrganizationSwitchModal from "@/views/shared/components/modals/OrganizationSwitchModal.vue";
import {
  issueFixRoute,
  issueIconClass,
  issueSeverityColor,
  issueSeverityBadgeClass,
  formatIssueTimeAgo,
} from "@/queries/partner-issues";

/**
 * Reusable "issues card list" — the canonical severity-coloured issue list used by the partner
 * dashboard widget, the client detail page, and anywhere a compact issues feed is needed. The
 * full Issues page renders the same data as a DataTable instead; both share the helpers in
 * `@/queries/partner-issues`.
 *
 * Clicking "Fix" prompts an org-switch into the client's org (via `OrganizationSwitchModal`),
 * routed at the issue's project. Pass `showViewAll` to render the "View all issues" footer link.
 */
const props = withDefaults(
  defineProps<{
    issues: PartnerIssue[];
    loading?: boolean;
    showViewAll?: boolean;
    /** Empty-state copy. Defaults to the "all systems running" success message. */
    emptyText?: string;
  }>(),
  { showViewAll: true },
);

const showSwitchModal = ref(false);
const selectedIssue = ref<PartnerIssue | null>(null);

function handleFixIssue(issue: PartnerIssue, event: Event) {
  event.stopPropagation();
  selectedIssue.value = issue;
  showSwitchModal.value = true;
}

function handleSwitchCancelled() {
  selectedIssue.value = null;
}

// Route to the tab most relevant to the issue type (run history / connections / wizard) after
// switching org — see issueFixRoute. Falls back to home when the issue has no project.
const switchTargetRoute = computed(() =>
  selectedIssue.value ? issueFixRoute(selectedIssue.value) : { name: "home" },
);
</script>

<template>
  <div class="issues-list-wrap">
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="issues.length === 0" class="text-center py-4">
      <div class="text-success">
        <i class="bi bi-check-circle-fill fs-1 mb-2"></i>
        <p class="mb-0">{{ emptyText ?? "No critical issues" }}</p>
        <small class="text-muted">All systems are running smoothly</small>
      </div>
    </div>

    <div v-else class="issues-list">
      <div
        v-for="issue in issues"
        :key="issue.id"
        class="card mb-3"
        :class="{
          'border-danger': issue.severity === 'critical',
          'border-warning': issue.severity === 'medium' || issue.severity === 'high',
        }"
      >
        <div class="card-body">
          <div class="d-flex align-items-start gap-3">
            <div class="issue-icon">
              <i
                :class="[issueIconClass(issue.type), issueSeverityColor(issue.severity)]"
                class="fs-5"
              ></i>
            </div>

            <div class="issue-content flex-grow-1">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <h3 class="issue-title mb-0">{{ issue.title }}</h3>
                <span class="badge text-uppercase" :class="issueSeverityBadgeClass(issue.severity)">
                  {{ issue.severity }}
                </span>
              </div>

              <p class="issue-description text-muted small mb-2">
                {{ issue.description }}
              </p>

              <div class="issue-meta d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center gap-2 small text-muted">
                  <span v-if="issue.projectName">
                    <i class="bi bi-folder me-1"></i>{{ issue.projectName }}
                  </span>
                  <span>{{ formatIssueTimeAgo(issue.dateOccurred) }}</span>
                </div>

                <div v-if="!issue.resolved" class="issue-actions">
                  <button class="bb-btn-secondary-cta btn-sm" @click="handleFixIssue(issue, $event)">
                    <i class="bi bi-wrench me-1"></i>Fix
                  </button>
                </div>
                <span v-else class="badge bg-success">
                  <i class="bi bi-check-circle me-1"></i>Resolved
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showViewAll && issues.length > 0" class="text-center pt-3 border-top">
      <router-link to="/partners/issues" class="bb-btn-secondary-cta btn-sm">
        <i class="bi bi-arrow-right me-1"></i>View All Issues
      </router-link>
    </div>
  </div>

  <OrganizationSwitchModal
    :show="showSwitchModal"
    @update:show="showSwitchModal = $event"
    :target-organization="{
      id: selectedIssue?.clientId || '',
      name: selectedIssue?.clientName || 'Client Organization',
    }"
    :target-route="switchTargetRoute"
    context="issue"
    @cancelled="handleSwitchCancelled"
  />
</template>

<style scoped lang="scss">
.issues-list-wrap {
  max-height: 500px;
  overflow-y: auto;
}

.issue-icon {
  min-width: 24px;
  text-align: center;
}

.issue-title {
  font-weight: 600;
  color: var(--bs-body-color);
  font-size: 0.875rem;
}

.issue-description {
  font-size: 0.8rem;
  line-height: 1.3;
}

.issue-meta {
  font-size: 0.75rem;
}

.badge {
  --bs-badge-font-size: 0.65rem;
  --bs-badge-font-weight: 600;
}

.btn-sm {
  --bs-btn-font-size: 0.75rem;
  --bs-btn-padding-y: 0.25rem;
  --bs-btn-padding-x: 0.5rem;
}
</style>
